import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  // Device Types state
  deviceTypes: [],
  selectedDeviceType: null,
  deviceTypesTotal: 0,
  deviceTypesPage: 1,
  deviceTypesPageSize: 10,
  deviceTypesFilterName: '',
  deviceTypesFilterIsActive: ['true'],
  deviceTypesSortColumn: 'id',
  deviceTypesSortDirection: 'asc',
  deviceTypesLoading: false,
  deviceTypesError: null,
  deviceTypesSuccessMessage: null,

  // Verify Modes state
  verifyModes: [],
  selectedVerifyMode: null,
  verifyModesTotal: 0,
  verifyModesPage: 1,
  verifyModesPageSize: 10,
  verifyModesFilterName: '',
  verifyModesFilterIsActive: ['true'],
  verifyModesSortColumn: 'id',
  verifyModesSortDirection: 'asc',
  verifyModesLoading: false,
  verifyModesError: null,
  verifyModesSuccessMessage: null,

  // Active tab
  activeTab: 0,
};

const deviceConfigSlice = createSlice({
  name: 'deviceConfig',
  initialState,
  reducers: {
    // Tab management
    setActiveTab: (state, action) => {
      state.activeTab = action.payload;
    },

    // ==================== Device Types Actions ====================
    // Fetch device types
    fetchDeviceTypes: (state) => {
      state.deviceTypesLoading = true;
      state.deviceTypesError = null;
    },
    fetchDeviceTypesSuccess: (state, action) => {
      state.deviceTypes = action.payload.data;
      state.deviceTypesTotal = action.payload.meta.total;
      state.deviceTypesLoading = false;
      state.deviceTypesError = null;
    },
    fetchDeviceTypesFailure: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesError = action.payload;
    },

    // Fetch device type by ID
    fetchDeviceTypeById: (state) => {
      state.deviceTypesLoading = true;
      state.deviceTypesError = null;
    },
    fetchDeviceTypeByIdSuccess: (state, action) => {
      state.selectedDeviceType = action.payload;
      state.deviceTypesLoading = false;
      state.deviceTypesError = null;
    },
    fetchDeviceTypeByIdFailure: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesError = action.payload;
    },

    // Add device type
    addDeviceType: (state) => {
      state.deviceTypesLoading = true;
      state.deviceTypesError = null;
    },
    addDeviceTypeSuccess: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesSuccessMessage = action.payload.message;
      state.deviceTypesError = null;
    },
    addDeviceTypeFailure: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesError = action.payload;
    },

    // Update device type
    updateDeviceType: (state) => {
      state.deviceTypesLoading = true;
      state.deviceTypesError = null;
    },
    updateDeviceTypeSuccess: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesSuccessMessage = action.payload.message;
      state.selectedDeviceType = null;
      state.deviceTypesError = null;
    },
    updateDeviceTypeFailure: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesError = action.payload;
    },

    // Delete device type
    deleteDeviceType: (state) => {
      state.deviceTypesLoading = true;
      state.deviceTypesError = null;
    },
    deleteDeviceTypeSuccess: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesSuccessMessage = action.payload.message;
      state.deviceTypesError = null;
    },
    deleteDeviceTypeFailure: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesError = action.payload;
    },

    // Bulk delete device types
    bulkDeleteDeviceTypes: (state) => {
      state.deviceTypesLoading = true;
      state.deviceTypesError = null;
    },
    bulkDeleteDeviceTypesSuccess: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesSuccessMessage = action.payload.message;
      state.deviceTypesError = null;
    },
    bulkDeleteDeviceTypesFailure: (state, action) => {
      state.deviceTypesLoading = false;
      state.deviceTypesError = action.payload;
    },

    // Device types UI state management
    setDeviceTypesPage: (state, action) => {
      state.deviceTypesPage = action.payload;
    },
    setDeviceTypesPageSize: (state, action) => {
      state.deviceTypesPageSize = action.payload;
      state.deviceTypesPage = 1;
    },
    setDeviceTypesFilterName: (state, action) => {
      state.deviceTypesFilterName = action.payload;
      state.deviceTypesPage = 1;
    },
    setDeviceTypesFilterIsActive: (state, action) => {
      state.deviceTypesFilterIsActive = action.payload;
      state.deviceTypesPage = 1;
    },
    setDeviceTypesSortColumn: (state, action) => {
      state.deviceTypesSortColumn = action.payload;
    },
    setDeviceTypesSortDirection: (state, action) => {
      state.deviceTypesSortDirection = action.payload;
    },
    clearDeviceTypesError: (state) => {
      state.deviceTypesError = null;
    },
    clearDeviceTypesSuccessMessage: (state) => {
      state.deviceTypesSuccessMessage = null;
    },

    // ==================== Verify Modes Actions ====================
    // Fetch verify modes
    fetchVerifyModes: (state) => {
      state.verifyModesLoading = true;
      state.verifyModesError = null;
    },
    fetchVerifyModesSuccess: (state, action) => {
      state.verifyModes = action.payload.data;
      state.verifyModesTotal = action.payload.meta.total;
      state.verifyModesLoading = false;
      state.verifyModesError = null;
    },
    fetchVerifyModesFailure: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesError = action.payload;
    },

    // Fetch verify mode by ID
    fetchVerifyModeById: (state) => {
      state.verifyModesLoading = true;
      state.verifyModesError = null;
    },
    fetchVerifyModeByIdSuccess: (state, action) => {
      state.selectedVerifyMode = action.payload;
      state.verifyModesLoading = false;
      state.verifyModesError = null;
    },
    fetchVerifyModeByIdFailure: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesError = action.payload;
    },

    // Add verify mode
    addVerifyMode: (state) => {
      state.verifyModesLoading = true;
      state.verifyModesError = null;
    },
    addVerifyModeSuccess: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesSuccessMessage = action.payload.message;
      state.verifyModesError = null;
    },
    addVerifyModeFailure: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesError = action.payload;
    },

    // Update verify mode
    updateVerifyMode: (state) => {
      state.verifyModesLoading = true;
      state.verifyModesError = null;
    },
    updateVerifyModeSuccess: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesSuccessMessage = action.payload.message;
      state.selectedVerifyMode = null;
      state.verifyModesError = null;
    },
    updateVerifyModeFailure: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesError = action.payload;
    },

    // Delete verify mode
    deleteVerifyMode: (state) => {
      state.verifyModesLoading = true;
      state.verifyModesError = null;
    },
    deleteVerifyModeSuccess: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesSuccessMessage = action.payload.message;
      state.verifyModesError = null;
    },
    deleteVerifyModeFailure: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesError = action.payload;
    },

    // Bulk delete verify modes
    bulkDeleteVerifyModes: (state) => {
      state.verifyModesLoading = true;
      state.verifyModesError = null;
    },
    bulkDeleteVerifyModesSuccess: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesSuccessMessage = action.payload.message;
      state.verifyModesError = null;
    },
    bulkDeleteVerifyModesFailure: (state, action) => {
      state.verifyModesLoading = false;
      state.verifyModesError = action.payload;
    },

    // Verify modes UI state management
    setVerifyModesPage: (state, action) => {
      state.verifyModesPage = action.payload;
    },
    setVerifyModesPageSize: (state, action) => {
      state.verifyModesPageSize = action.payload;
      state.verifyModesPage = 1;
    },
    setVerifyModesFilterName: (state, action) => {
      state.verifyModesFilterName = action.payload;
      state.verifyModesPage = 1;
    },
    setVerifyModesFilterIsActive: (state, action) => {
      state.verifyModesFilterIsActive = action.payload;
      state.verifyModesPage = 1;
    },
    setVerifyModesSortColumn: (state, action) => {
      state.verifyModesSortColumn = action.payload;
    },
    setVerifyModesSortDirection: (state, action) => {
      state.verifyModesSortDirection = action.payload;
    },
    clearVerifyModesError: (state) => {
      state.verifyModesError = null;
    },
    clearVerifyModesSuccessMessage: (state) => {
      state.verifyModesSuccessMessage = null;
    },
  },
});

export const {
  setActiveTab,
  // Device Types actions
  fetchDeviceTypes,
  fetchDeviceTypesSuccess,
  fetchDeviceTypesFailure,
  fetchDeviceTypeById,
  fetchDeviceTypeByIdSuccess,
  fetchDeviceTypeByIdFailure,
  addDeviceType,
  addDeviceTypeSuccess,
  addDeviceTypeFailure,
  updateDeviceType,
  updateDeviceTypeSuccess,
  updateDeviceTypeFailure,
  deleteDeviceType,
  deleteDeviceTypeSuccess,
  deleteDeviceTypeFailure,
  bulkDeleteDeviceTypes,
  bulkDeleteDeviceTypesSuccess,
  bulkDeleteDeviceTypesFailure,
  setDeviceTypesPage,
  setDeviceTypesPageSize,
  setDeviceTypesFilterName,
  setDeviceTypesFilterIsActive,
  setDeviceTypesSortColumn,
  setDeviceTypesSortDirection,
  clearDeviceTypesError,
  clearDeviceTypesSuccessMessage,
  // Verify Modes actions
  fetchVerifyModes,
  fetchVerifyModesSuccess,
  fetchVerifyModesFailure,
  fetchVerifyModeById,
  fetchVerifyModeByIdSuccess,
  fetchVerifyModeByIdFailure,
  addVerifyMode,
  addVerifyModeSuccess,
  addVerifyModeFailure,
  updateVerifyMode,
  updateVerifyModeSuccess,
  updateVerifyModeFailure,
  deleteVerifyMode,
  deleteVerifyModeSuccess,
  deleteVerifyModeFailure,
  bulkDeleteVerifyModes,
  bulkDeleteVerifyModesSuccess,
  bulkDeleteVerifyModesFailure,
  setVerifyModesPage,
  setVerifyModesPageSize,
  setVerifyModesFilterName,
  setVerifyModesFilterIsActive,
  setVerifyModesSortColumn,
  setVerifyModesSortDirection,
  clearVerifyModesError,
  clearVerifyModesSuccessMessage,
} = deviceConfigSlice.actions;

// Selectors - Device Types
export const selectActiveTab = (state) => state.deviceConfig?.activeTab || 0;
export const selectDeviceTypes = (state) => state.deviceConfig?.deviceTypes || [];
export const selectSelectedDeviceType = (state) => state.deviceConfig?.selectedDeviceType;
export const selectDeviceTypesLoading = (state) => state.deviceConfig?.deviceTypesLoading || false;
export const selectDeviceTypesError = (state) => state.deviceConfig?.deviceTypesError;
export const selectDeviceTypesSuccessMessage = (state) => state.deviceConfig?.deviceTypesSuccessMessage;
export const selectDeviceTypesTotal = (state) => state.deviceConfig?.deviceTypesTotal || 0;
export const selectDeviceTypesPage = (state) => state.deviceConfig?.deviceTypesPage || 1;
export const selectDeviceTypesPageSize = (state) => state.deviceConfig?.deviceTypesPageSize || 10;
export const selectDeviceTypesSortColumn = (state) => state.deviceConfig?.deviceTypesSortColumn || 'id';
export const selectDeviceTypesSortDirection = (state) => state.deviceConfig?.deviceTypesSortDirection || 'asc';
export const selectDeviceTypesFilterName = (state) => state.deviceConfig?.deviceTypesFilterName || '';
export const selectDeviceTypesFilterIsActive = (state) => state.deviceConfig?.deviceTypesFilterIsActive;

// Selectors - Verify Modes
export const selectVerifyModes = (state) => state.deviceConfig?.verifyModes || [];
export const selectSelectedVerifyMode = (state) => state.deviceConfig?.selectedVerifyMode;
export const selectVerifyModesLoading = (state) => state.deviceConfig?.verifyModesLoading || false;
export const selectVerifyModesError = (state) => state.deviceConfig?.verifyModesError;
export const selectVerifyModesSuccessMessage = (state) => state.deviceConfig?.verifyModesSuccessMessage;
export const selectVerifyModesTotal = (state) => state.deviceConfig?.verifyModesTotal || 0;
export const selectVerifyModesPage = (state) => state.deviceConfig?.verifyModesPage || 1;
export const selectVerifyModesPageSize = (state) => state.deviceConfig?.verifyModesPageSize || 10;
export const selectVerifyModesSortColumn = (state) => state.deviceConfig?.verifyModesSortColumn || 'id';
export const selectVerifyModesSortDirection = (state) => state.deviceConfig?.verifyModesSortDirection || 'asc';
export const selectVerifyModesFilterName = (state) => state.deviceConfig?.verifyModesFilterName || '';
export const selectVerifyModesFilterIsActive = (state) => state.deviceConfig?.verifyModesFilterIsActive;

export default deviceConfigSlice.reducer;
